<template>
    <div class="dashboard-container">
        <b-card no-body class="main-card">
            <!-- HEADER SECTION -->
            <b-card-body class="header-body py-3">
                <div class="d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center">
                        <div class="header-icon-wrapper mr-3">
                            <b-icon-people class="header-icon" />
                        </div>
                        <div>
                            <h5 class="mb-0 header-title">REPORTS - SALESPEOPLE</h5>
                            <small class="header-subtitle" v-if="picked_sale_type === 'layaway'">
                                <b-icon-basket class="mr-1" />
                                Currently viewing LAYAWAY SALES only
                            </small>
                            <small class="header-subtitle" v-else-if="picked_sale_type === 'regular'">
                                <b-icon-receipt class="mr-1" />
                                Currently viewing REGULAR SALES only
                            </small>
                            <small class="header-subtitle" v-else>
                                <b-icon-bar-chart-line class="mr-1" />
                                Viewing ALL SALE TYPES
                            </small>
                        </div>
                    </div>
                    <div class="d-flex align-items-center">
                        <b-badge variant="light" class="mr-2 date-badge">
                            <b-icon-calendar class="mr-1" />{{ getDateRangeText() }}
                        </b-badge>
                    </div>
                </div>
            </b-card-body>

            <!-- FILTERS SECTION -->
            <b-card no-body class="filters-card">
                <b-card-body class="py-3">
                    <div class="row align-items-end">
                        <div class="col-lg-8">
                            <div class="d-flex flex-wrap align-items-end" style="gap: 16px;">
                                <div style="min-width: 320px;">
                                    <label class="small text-muted mb-2 filter-label">
                                        <b-icon-calendar class="mr-1" size="sm" />DATE RANGE
                                    </label>
                                    <date-picker
                                        v-model="date_range"
                                        format="MM-DD-YYYY"
                                        valueType="format"
                                        class="custom-datepicker"
                                        placeholder="SELECT DATE RANGE"
                                        range
                                    ></date-picker>
                                </div>

                                <div class="d-flex flex-wrap" style="gap: 8px;">
                                    <b-button size="md" class="quick-date-btn py-2 px-3"
                                        :variant="picked_date_range=='today' ? 'danger' : 'outline-danger'"
                                        @click="set_dates('today')">
                                        <b-icon-calendar-day class="mr-1" size="sm" />Today
                                    </b-button>
                                    <b-button size="md" class="quick-date-btn py-2 px-3"
                                        :variant="picked_date_range=='yesterday' ? 'danger' : 'outline-danger'"
                                        @click="set_dates('yesterday')">
                                        <b-icon-calendar-day class="mr-1" size="sm" />Yesterday
                                    </b-button>
                                    <b-button size="md" class="quick-date-btn py-2 px-3"
                                        :variant="picked_date_range=='this_week' ? 'danger' : 'outline-danger'"
                                        @click="set_dates('this_week')">
                                        <b-icon-calendar-week class="mr-1" size="sm" />This Week
                                    </b-button>
                                    <b-button size="md" class="quick-date-btn py-2 px-3"
                                        :variant="picked_date_range=='last_week' ? 'danger' : 'outline-danger'"
                                        @click="set_dates('last_week')">
                                        <b-icon-calendar-week class="mr-1" size="sm" />Last Week
                                    </b-button>
                                    <b-button size="md" class="quick-date-btn py-2 px-3"
                                        :variant="picked_date_range=='this_month' ? 'danger' : 'outline-danger'"
                                        @click="set_dates('this_month')">
                                        <b-icon-calendar-month class="mr-1" size="sm" />This Month
                                    </b-button>
                                    <b-button size="md" class="quick-date-btn py-2 px-3"
                                        :variant="picked_date_range=='last_month' ? 'danger' : 'outline-danger'"
                                        @click="set_dates('last_month')">
                                        <b-icon-calendar-month class="mr-1" size="sm" />Last Month
                                    </b-button>
                                </div>
                            </div>
                        </div>

                        <div class="col-lg-4 mt-3 mt-lg-0">
                            <div class="d-flex justify-content-end">
                                <b-button variant="success" @click="fetch_init" class="search-btn" :disabled="items_being_loaded">
                                    <b-icon-search class="mr-2" />
                                    <span v-if="!items_being_loaded">SEARCH</span>
                                    <span v-else>
                                        <b-icon-arrow-repeat spin class="mr-2" />LOADING...
                                    </span>
                                </b-button>
                            </div>
                        </div>
                    </div>

                    <!-- ADVANCED FILTERS -->
                    <div class="mt-3">
                        <b-button variant="link" size="md" @click="showAdvancedFilters = !showAdvancedFilters" class="p-0 text-decoration-none advanced-toggle">
                            <b-icon :icon="showAdvancedFilters ? 'chevron-up' : 'chevron-down'" class="mr-1" size="sm" />
                            {{ showAdvancedFilters ? 'Hide' : 'Show' }} Filters
                        </b-button>

                        <b-collapse v-model="showAdvancedFilters">
                            <div class="mt-3 pt-3 border-top">
                                <div class="row">
                                    <div class="col-md-3 mb-3">
                                        <label class="small text-muted mb-2 filter-label">
                                            <b-icon-file-text class="mr-1" size="sm" />REPORT TYPE
                                        </label>
                                        <b-form-select :options="details" v-model="details_filter" size="md" class="filter-select" @change="fetch_init">
                                            <template #first>
                                                <b-form-select-option value="null">SELECT ANY</b-form-select-option>
                                            </template>
                                        </b-form-select>
                                    </div>
                                    <div class="col-md-3 mb-3">
                                        <label class="small text-muted mb-2 filter-label">
                                            <b-icon-building class="mr-1" size="sm" />LOCATION
                                        </label>
                                        <b-form-select :options="filteredLocations" v-model="picked_location_id" size="md" class="filter-select" @change="fetch_init">
                                            <template #first>
                                                <b-form-select-option :value="null">All Locations</b-form-select-option>
                                            </template>
                                        </b-form-select>
                                    </div>
                                    <div class="col-md-3 mb-3">
                                        <label class="small text-muted mb-2 filter-label">
                                            <b-icon-person class="mr-1" size="sm" />SALESPERSON
                                        </label>
                                        <b-form-select :options="salespersonOptions" v-model="picked_salesperson" size="md" class="filter-select" @change="fetch_init">
                                            <template #first>
                                                <b-form-select-option :value="null">All Salespeople</b-form-select-option>
                                            </template>
                                        </b-form-select>
                                    </div>
                                    <div class="col-md-3 mb-3">
                                        <label class="small text-muted mb-2 filter-label">
                                            <b-icon-receipt class="mr-1" size="sm" />INVOICE TYPE
                                        </label>
                                        <b-form-select :options="invoice_options" v-model="picked_invoice_type" size="md" class="filter-select" @change="fetch_init()">
                                            <template #first>
                                                <b-form-select-option value="null">All Types</b-form-select-option>
                                            </template>
                                        </b-form-select>
                                    </div>
                                    <div class="col-md-3 mb-3">
                                        <label class="small text-muted mb-2 filter-label">
                                            <b-icon-tag class="mr-1" size="sm" />SALE TYPE
                                        </label>
                                        <b-form-select :options="sale_type_options" v-model="picked_sale_type" size="md" class="filter-select" @change="fetch_init()">
                                            <template #first>
                                                <b-form-select-option value="null">All Sale Types</b-form-select-option>
                                            </template>
                                        </b-form-select>
                                    </div>
                                </div>
                            </div>
                        </b-collapse>
                    </div>
                </b-card-body>
            </b-card>

            <!-- ============================================== -->
            <!-- COMPARISON CARDS - REFERENCE STYLE (OPTION A) -->
            <!-- ============================================== -->
            <div class="comparison-grid">
                <!-- Sales Comparison Card (DARK) -->
                <div class="comparison-card dark accent-green">
                    <div class="comparison-header">
                        <div class="comparison-icon-wrap icon-green">
                            <b-icon-graph-up />
                        </div>
                        <h6 class="comparison-title">SALES</h6>
                    </div>

                    <div class="periods-row">
                        <div class="period-box current-period">
                            <div class="period-label">Current Period</div>
                            <div class="period-value primary-value">{{ formatCurrency(salesComparison.currentTotal) }}</div>
                        </div>
                        <div class="period-box previous-period">
                            <div class="period-label">Previous Period</div>
                            <div class="period-value secondary-value">{{ formatCurrency(salesComparison.previousTotal) }}</div>
                        </div>
                    </div>

                    <div class="metrics-row">
                        <div class="metric-box">
                            <div class="metric-label">Difference</div>
                            <div class="metric-value" :class="salesComparison.trend === 'increase' ? 'text-success' : salesComparison.trend === 'decrease' ? 'text-danger' : 'text-muted'">
                                {{ formatCurrency(Math.abs(salesComparison.difference)) }}
                            </div>
                        </div>
                        <div class="metric-box">
                            <div class="metric-label">Growth</div>
                            <div class="metric-value" :class="salesComparison.trend === 'increase' ? 'text-success' : salesComparison.trend === 'decrease' ? 'text-danger' : 'text-muted'">
                                {{ salesComparison.percentage.toFixed(1) }}%
                                <b-icon-arrow-up v-if="salesComparison.trend === 'increase'" />
                                <b-icon-arrow-down v-else-if="salesComparison.trend === 'decrease'" />
                                <b-icon-dash v-else />
                            </div>
                        </div>
                    </div>

                    <div class="date-info">
                        <strong>Current:</strong> {{ getDateRangeText() }}<br>
                        <strong>Previous:</strong> {{ getPreviousDateRangeText() }}
                    </div>
                </div>

                <!-- Refunds Comparison Card -->
                <div class="comparison-card accent-red">
                    <div class="comparison-header">
                        <div class="comparison-icon-wrap icon-red">
                            <b-icon-arrow-return-left />
                        </div>
                        <h6 class="comparison-title">REFUNDS</h6>
                    </div>

                    <div class="periods-row">
                        <div class="period-box current-period">
                            <div class="period-label">Current Period</div>
                            <div class="period-value primary-value refund-value">{{ formatCurrency(refundsComparison.currentTotal) }}</div>
                        </div>
                        <div class="period-box previous-period">
                            <div class="period-label">Previous Period</div>
                            <div class="period-value secondary-value">{{ formatCurrency(refundsComparison.previousTotal) }}</div>
                        </div>
                    </div>

                    <div class="metrics-row">
                        <div class="metric-box">
                            <div class="metric-label">Difference</div>
                            <div class="metric-value" :class="refundsComparison.trend === 'increase' ? 'text-danger' : refundsComparison.trend === 'decrease' ? 'text-success' : 'text-muted'">
                                {{ formatCurrency(Math.abs(refundsComparison.difference)) }}
                            </div>
                        </div>
                        <div class="metric-box">
                            <div class="metric-label">Change</div>
                            <div class="metric-value" :class="refundsComparison.trend === 'increase' ? 'text-danger' : refundsComparison.trend === 'decrease' ? 'text-success' : 'text-muted'">
                                {{ refundsComparison.percentage.toFixed(1) }}%
                                <b-icon-arrow-up v-if="refundsComparison.trend === 'increase'" />
                                <b-icon-arrow-down v-else-if="refundsComparison.trend === 'decrease'" />
                                <b-icon-dash v-else />
                            </div>
                        </div>
                    </div>

                    <div class="date-info">
                        <strong>Current:</strong> {{ getDateRangeText() }}<br>
                        <strong>Previous:</strong> {{ getPreviousDateRangeText() }}
                    </div>
                </div>

                <!-- Layaway Comparison Card -->
                <div class="comparison-card accent-amber">
                    <div class="comparison-header">
                        <div class="comparison-icon-wrap icon-amber">
                            <b-icon-basket />
                        </div>
                        <h6 class="comparison-title">LAYAWAY</h6>
                    </div>

                    <div class="periods-row">
                        <div class="period-box current-period">
                            <div class="period-label">Current Period</div>
                            <div class="period-value primary-value layaway-value">{{ formatCurrency(layawayComparison.currentTotal) }}</div>
                        </div>
                        <div class="period-box previous-period">
                            <div class="period-label">Previous Period</div>
                            <div class="period-value secondary-value">{{ formatCurrency(layawayComparison.previousTotal) }}</div>
                        </div>
                    </div>

                    <div class="metrics-row">
                        <div class="metric-box">
                            <div class="metric-label">Difference</div>
                            <div class="metric-value" :class="layawayComparison.trend === 'increase' ? 'text-success' : layawayComparison.trend === 'decrease' ? 'text-danger' : 'text-muted'">
                                {{ formatCurrency(Math.abs(layawayComparison.difference)) }}
                            </div>
                        </div>
                        <div class="metric-box">
                            <div class="metric-label">Growth</div>
                            <div class="metric-value" :class="layawayComparison.trend === 'increase' ? 'text-success' : layawayComparison.trend === 'decrease' ? 'text-danger' : 'text-muted'">
                                {{ layawayComparison.percentage.toFixed(1) }}%
                                <b-icon-arrow-up v-if="layawayComparison.trend === 'increase'" />
                                <b-icon-arrow-down v-else-if="layawayComparison.trend === 'decrease'" />
                                <b-icon-dash v-else />
                            </div>
                        </div>
                    </div>

                    <div class="date-info">
                        <strong>Current:</strong> {{ getDateRangeText() }}<br>
                        <strong>Previous:</strong> {{ getPreviousDateRangeText() }}
                    </div>
                </div>
            </div>

            <!-- LOADING STATE FOR COMPARISON -->
            <div v-if="comparisonLoading" class="text-center py-4">
                <b-spinner variant="primary" type="grow" style="width: 3rem; height: 3rem;"></b-spinner>
                <p class="mt-3 text-muted" style="font-size: 18px;">Loading comparison data...</p>
            </div>

            <!-- DATA TABLE -->
            <div v-if="items_being_loaded" class="text-center py-5">
                <b-spinner variant="primary" type="grow" style="width: 3rem; height: 3rem;"></b-spinner>
                <p class="mt-3 text-muted" style="font-size: 18px;">Fetching records...</p>
            </div>

            <template v-else-if="items.length > 0">
                <b-card no-body class="data-table-card">
                    <b-card-header class="bg-light d-flex justify-content-between align-items-center py-2">
                        <div class="d-flex align-items-center">
                            <b-icon-table class="mr-2" size="sm" />
                            <h6 class="mb-0 table-title">{{ getTableTitle() }}</h6>
                            <b-badge variant="secondary" class="ml-2 table-badge">{{ filteredItems.length }} records</b-badge>
                            <b-badge v-if="picked_sale_type === 'layaway'" variant="warning" class="ml-2 table-badge">
                                <b-icon-basket size="sm" class="mr-1" />Layaway Sales
                            </b-badge>
                            <b-badge v-else-if="picked_sale_type === 'regular'" variant="primary" class="ml-2 table-badge">
                                <b-icon-receipt size="sm" class="mr-1" />Regular Sales
                            </b-badge>
                        </div>
                        <div class="text-muted" style="font-size: 16px;">
                            {{ getDateRangeText() }}
                        </div>
                    </b-card-header>

                    <div class="table-wrapper-sm">
                        <table class="compact-table">
                            <thead>
                                <tr>
                                    <th v-for="col in allowed_cols()" :key="col"
                                        :class="getHeaderClass(col)"
                                        :style="getHeaderStyle(col)">
                                        <div class="d-flex align-items-center justify-content-between">
                                            <span>{{ (fixed_columns_dict[col] || col ) }}</span>
                                            <b-button
                                                v-if="isSortableColumn(col)"
                                                variant="link"
                                                size="sm"
                                                class="p-0 ml-1 text-muted sort-btn"
                                                @click="sortByColumn(col)"
                                            >
                                                <b-icon-arrow-up-short v-if="sortColumn === col && sortDirection === 'asc'" size="sm" />
                                                <b-icon-arrow-down-short v-else-if="sortColumn === col && sortDirection === 'desc'" size="sm" />
                                                <b-icon-arrow-up-short v-else size="sm" style="opacity: 0.3;" />
                                            </b-button>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, idx) in paginatedItems" :key="idx" :class="getRowClass(item)">
                                    <td v-for="row_col in allowed_cols()" :key="row_col"
                                        :class="getCellClass(row_col, item[row_col])">
                                        <span v-if="row_col === 'salesperson'">
                                            {{ formatSalespersonName(item[row_col]) }}
                                        </span>
                                        <span v-else-if="row_col === 'location_name'">
                                            {{ packet.locationsdict[item.location_id] || `Location ${item.location_id}` }}
                                        </span>
                                        <span v-else-if="['invoice_id', 'lease_ids', 'lease_company'].includes(row_col)">
                                            {{ item[row_col] || '—' }}
                                        </span>
                                        <span v-else-if="row_col === 'total_sales_count'">
                                            {{ formatSalesCount(item[row_col]) }}
                                        </span>
                                        <span v-else-if="row_col === 'is_layaway'">
                                            <b-badge :variant="item[row_col] ? 'warning' : 'light'" pill class="delivery-badge">
                                                {{ item[row_col] ? 'Layaway' : 'Regular' }}
                                            </b-badge>
                                        </span>
                                        <span v-else-if="row_col === 'net_sales' || row_col === 'layaway_paid_so_far'">
                                            {{ formatCurrency(item[row_col]) }}
                                        </span>
                                        <span v-else-if="typeof item[row_col] === 'number'">
                                            {{ formatCurrency(item[row_col]) }}
                                        </span>
                                        <span v-else>
                                            {{ (item[row_col] || 0.0).toFixed(2) }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot v-if="showFooter" class="totals-row">
                                <tr>
                                    <td v-for="col in allowed_cols()" :key="'footer-' + col" class="fw-bold">
                                        <span v-if="col === 'salesperson'">TOTAL</span>
                                        <span v-else-if="col === 'locuser'">—</span>
                                        <span v-else-if="col === 'total_sales_count'">
                                            {{ formatSalesCount(columnTotal(col)) }}
                                        </span>
                                        <span v-else-if="col === 'is_layaway'">—</span>
                                        <span v-else-if="isNumericColumn(col)">
                                            {{ formatCurrency(columnTotal(col)) }}
                                        </span>
                                        <span v-else>—</span>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </b-card>

                <!-- Pagination -->
                <div v-if="filteredItems.length > perPage" class="pagination-wrapper d-flex justify-content-between align-items-center p-3 bg-light">
                    <small class="text-muted" style="font-size: 16px;">
                        Showing {{ paginationStart }} to {{ paginationEnd }} of {{ filteredItems.length }} records
                    </small>
                    <b-pagination
                        v-model="currentPage"
                        :total-rows="filteredItems.length"
                        :per-page="perPage"
                        size="md"
                        class="mb-0"
                        align="center"
                    ></b-pagination>
                    <b-form-select v-model="perPage" size="md" style="width: 85px;" @change="currentPage = 1">
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                    </b-form-select>
                </div>
            </template>

            <!-- EMPTY STATE -->
            <div v-else-if="!items_being_loaded" class="text-center py-5">
                <b-alert show variant="warning" class="mb-0" style="font-size: 18px;">
                    <b-icon-inbox class="mr-2" />
                    <strong>No records found</strong>
                    <p class="mb-0 mt-2">Try adjusting your filters or date range</p>
                    <p v-if="picked_sale_type === 'layaway'" class="mb-0">
                        <small>No layaway sales found for the selected criteria</small>
                    </p>
                </b-alert>
            </div>
        </b-card>
    </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import 'moment/locale/en-gb';
import global_vars from '@/utilities/global_vars';
import Backer from '@/components/globals/Backer';
import Requestor from '@/utilities/Requestor';
import JsonCSV from 'vue-json-csv';

import {
  BIconPeople,
  BIconCalendar,
  BIconCalendarDay,
  BIconCalendarWeek,
  BIconCalendarMonth,
  BIconSearch,
  BIconTable,
  BIconArrowRepeat,
  BIconDownload,
  BIconBuilding,
  BIconTag,
  BIconInbox,
  BIconFileText,
  BIconReceipt,
  BIconArrowUpShort,
  BIconArrowDownShort,
  BIconArrowUp,
  BIconArrowDown,
  BIconDash,
  BIconFilterCircle,
  BIconChevronUp,
  BIconChevronDown,
  BIconPerson,
  BIconGraphUp,
  BIconArrowReturnLeft,
  BIconBasket,
  BIconBarChartLine
} from 'bootstrap-vue';

moment.locale('en-gb');

export default {
    name: "ReportsUsersSale",
    components: {
        Backer, DatePicker, DownloadCsv: JsonCSV,
        BIconPeople, BIconCalendar, BIconCalendarDay, BIconCalendarWeek, BIconCalendarMonth,
        BIconSearch, BIconTable, BIconArrowRepeat, BIconDownload, BIconBuilding, BIconTag,
        BIconInbox, BIconFileText, BIconReceipt, BIconArrowUpShort, BIconArrowDownShort,
        BIconArrowUp, BIconArrowDown, BIconDash, BIconFilterCircle, BIconChevronUp,
        BIconChevronDown, BIconPerson, BIconGraphUp, BIconArrowReturnLeft, BIconBasket,
        BIconBarChartLine
    },
    data: function(){
        return {
            packet: {},
            items: [],
            items_being_loaded: false,
            global_vars: global_vars,
            date_range: [],
            picked_date_range: null,
            picked_salesperson: null,
            salespersonOptions: [],
            sale_type_options: [
                {value: null, text: "ALL SALE TYPES"},
                {value: 'regular', text: "REGULAR SALES"},
                {value: 'layaway', text: "LAYAWAY SALES"}
            ],
            picked_sale_type: null,
            details: [
                {value: 1, text: "SUMMARY"},
                {value: 2, text: "DETAILED REFUNDS"},
                {value: 3, text: "DETAILED SALES"},
                {value: 4, text: "PER INVOICE SALES"},
            ],
            invoice_options: [
                {value: null, text: "ALL"},
                {value: 1, text: "WRITTEN"},
                {value: 2, text: "DELIVERED"},
            ],
            fixed_columns_dict: {
                salesperson: "SALESPERSON",
                locuser: "LOCATION | SALESPERSON",
                location_name: "LOCATION",
                total_sales_count: "NO OF SALES",
                total_without_tax: "GROSS SALES",
                total_refunded_without_tax: "TOTAL REFUNDED",
                total_short_amount: "SHORT AMOUNT",
                net_sales: "NET SALES",
                total_sales_tax: "SLS TAX",
                total_refunded_st: "SLS TAX REFUNDED",
                total_cc_sales: "CC SALES",
                total_finance_sales: "FIN SALES",
                total_cash_sales: "CASH SALES",
                total_cc_refunds: "CC REFUNDS",
                total_finance_refunds: "FIN REFUNDS",
                total_cash_refunds: "CASH REFUNDS",
                invoice_id: "INVOICE #",
                lease_ids: "LEASE IDS",
                lease_company: "LEASE COMPANY",
                is_layaway: "SALE TYPE",
                layaway_sales_amount: "LAYAWAY TOTAL",
                layaway_paid_so_far: "LAYAWAY PAID",
                regular_sales_amount: "REGULAR SALES"
            },
            columns: [],
            picked_location_id: null,
            details_filter: null,
            picked_invoice_type: null,
            currentPage: 1,
            perPage: 50,
            sortColumn: null,
            sortDirection: 'asc',
            comparisonData: null,
            comparisonLoading: false,
            previousPeriodData: null,
            showAdvancedFilters: false
        }
    },
    computed: {
        itemsWithCalculations() {
            return this.items.map(item => {
                const grossSales = parseFloat(item.total_without_tax || 0);
                const refunds = parseFloat(item.total_refunded_without_tax || 0);
                const netSales = grossSales - refunds;

                let layawayPaidSoFar = 0;
                if (item.is_layaway || item.was_layaway || item.layaway_sales_amount) {
                    layawayPaidSoFar = grossSales - (item.layaway_balance_remaining || 0);
                }

                return {
                    ...item,
                    net_sales: netSales,
                    layaway_paid_so_far: layawayPaidSoFar
                };
            });
        },

        itemsWithLocationNames() {
            return this.items.map(item => ({
                ...item,
                location_name: this.packet.locationsdict[item.location_id] || `Location ${item.location_id}`
            }));
        },

        filteredLocations() {
            if (!this.packet.locations) return [];
            const excludedLocationNames = [
                'FOLCROFT WH', '101 WH', '9TH ST WH', 'OUT OF STOCK',
                'MECCA SHIPPING', 'ONLINE SALES'
            ];
            return this.packet.locations.filter(location =>
                !excludedLocationNames.includes(location.text)
            );
        },

        salespeople() {
            if (this.items.length > 0) {
                const salespeople = [...new Set(this.items.map(item => item.salesperson))];
                return salespeople.map(sp => ({
                    value: sp,
                    text: this.formatSalespersonName(sp)
                })).sort((a, b) => a.text.localeCompare(b.text));
            }
            return [];
        },

        filteredItems() {
            return this.items.filter(item => this.allowed_row(item));
        },

        paginatedItems() {
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            return this.sortedItems.slice(start, end);
        },

        sortedItems() {
            if (!this.sortColumn) return this.filteredItems;
            return [...this.filteredItems].sort((a, b) => {
                let aVal = a[this.sortColumn];
                let bVal = b[this.sortColumn];
                if (this.isNumericColumn(this.sortColumn)) {
                    aVal = parseFloat(aVal || 0);
                    bVal = parseFloat(bVal || 0);
                }
                if (this.sortColumn === 'salesperson') {
                    aVal = this.formatSalespersonName(aVal);
                    bVal = this.formatSalespersonName(bVal);
                }
                if (this.sortColumn === 'locuser') {
                    aVal = this.formatLocationUser(a);
                    bVal = this.formatLocationUser(b);
                }
                if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
                if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
                return 0;
            });
        },

        totalPages() {
            return Math.ceil(this.filteredItems.length / this.perPage);
        },

        paginationStart() {
            return (this.currentPage - 1) * this.perPage + 1;
        },

        paginationEnd() {
            const end = this.currentPage * this.perPage;
            return end > this.filteredItems.length ? this.filteredItems.length : end;
        },

        showFooter() {
            return this.details_filter !== 4 && this.filteredItems.length > 0;
        },

        salesComparison() {
            return this.calculateComparison('total_without_tax');
        },

        refundsComparison() {
            return this.calculateComparison('total_refunded_without_tax');
        },

        layawayComparison() {
            if (!this.items.length || !this.previousPeriodData) {
                return this.getEmptyComparison();
            }
            const currentLayawayTotal = this.items.reduce((sum, item) =>
                sum + parseFloat(item.layaway_sales_amount || 0), 0
            );
            const previousLayawayTotal = this.previousPeriodData.reduce((sum, item) => {
                if (item.layaway_sales_amount !== undefined) {
                    return sum + parseFloat(item.layaway_sales_amount || 0);
                } else {
                    return sum + ((item.is_layaway === true || item.was_layaway === true) ? parseFloat(item.total_without_tax || 0) : 0);
                }
            }, 0);
            const difference = currentLayawayTotal - previousLayawayTotal;
            const percentage = previousLayawayTotal > 0 ? (difference / previousLayawayTotal) * 100 :
                             currentLayawayTotal > 0 ? 100 : 0;
            return {
                currentTotal: currentLayawayTotal,
                previousTotal: previousLayawayTotal,
                difference: difference,
                percentage: Math.abs(percentage),
                trend: difference > 0 ? 'increase' : difference < 0 ? 'decrease' : 'same'
            };
        }
    },
    watch: {
        currentPage() {
            this.$nextTick(() => {
                const table = this.$el.querySelector('.data-table-card');
                if (table) {
                    table.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            });
        },
        filteredItems() {
            this.currentPage = 1;
        },
        items() {
            this.salespersonOptions = this.salespeople;
        }
    },
    methods: {
        calculateComparison(field) {
            if (!this.items.length || !this.previousPeriodData) {
                return this.getEmptyComparison();
            }
            const currentTotal = this.items.reduce((sum, item) =>
                sum + parseFloat(item[field] || 0), 0
            );
            const previousTotal = this.previousPeriodData.reduce((sum, item) =>
                sum + parseFloat(item[field] || 0), 0
            );
            const difference = currentTotal - previousTotal;
            const percentage = previousTotal > 0 ? (difference / previousTotal) * 100 :
                             currentTotal > 0 ? 100 : 0;
            return {
                currentTotal,
                previousTotal,
                difference: difference,
                percentage: Math.abs(percentage),
                trend: difference > 0 ? 'increase' : difference < 0 ? 'decrease' : 'same'
            };
        },

        getEmptyComparison() {
            return {
                currentTotal: 0,
                previousTotal: 0,
                difference: 0,
                percentage: 0,
                trend: 'same'
            };
        },

        allowed_cols: function(){
            let cols = [];
            if (this.details_filter == 1){
                cols = [
                    'salesperson', 'location_name', 'total_sales_count',
                    'total_without_tax', 'net_sales', 'total_short_amount',
                    'total_refunded_without_tax', 'total_sales_tax',
                    'total_refunded_st', 'total_cc_sales', 'total_finance_sales',
                    'total_cash_sales'
                ];
                if (this.items.some(item => item.layaway_sales_amount !== undefined)) {
                    cols.push('layaway_sales_amount', 'layaway_paid_so_far', 'regular_sales_amount');
                } else {
                    cols.push('is_layaway');
                }
            }
            if (this.details_filter == 2){
                cols = [
                    'salesperson', 'location_name', 'total_sales_count',
                    'total_without_tax', 'net_sales', 'total_refunded_without_tax',
                    'total_sales_tax', 'total_refunded_st'
                ];
                let total_refunds_prefixes = this.columns.filter(function(d){ return d.split("_")[0] == "total" && d.split("_")[2] == "refunds"});
                cols = cols.concat(total_refunds_prefixes);
                let total_fsg_refunds =  this.columns.filter(function(d){ return d.split(" ")[0] == "REFUNDS"});
                cols = cols.concat(total_fsg_refunds);
                cols.push('is_layaway');
            }
            if (this.details_filter == 3){
                cols = [
                    'salesperson', 'location_name', 'total_sales_count',
                    'total_without_tax', 'net_sales', 'total_refunded_without_tax',
                    'total_sales_tax', 'total_refunded_st'
                ];
                let total_sales_prefixes = this.columns.filter(function(d){ return d.split("_")[0] == "total" && d.split("_")[2] == "sales"});
                cols = cols.concat(total_sales_prefixes);
                let total_fsg_sales =  this.columns.filter(function(d){ return d.split(" ")[0] == "SALES"});
                cols = cols.concat(total_fsg_sales);
                cols.push('is_layaway');
            }
            if (this.details_filter == 4) {
                cols = [
                    'salesperson', 'location_name', 'total_sales_count',
                    'invoice_id', 'lease_ids', 'lease_company',
                    'total_without_tax', 'net_sales', 'total_refunded_without_tax',
                    'total_sales_tax', 'total_refunded_st',
                    'total_cash_sales', 'total_cc_sales', 'total_finance_sales',
                    'is_layaway'
                ];
                const dynamicSales = this.columns.filter(d => d.startsWith('SALES ('));
                cols = cols.concat(dynamicSales);
            }
            return cols;
        },

        allowed_row: function(item){
            let status = true;
            if (this.picked_location_id != null){
                if (item.location_id != this.picked_location_id){
                    status = false;
                }
            }
            if (this.picked_salesperson != null){
                if (item.salesperson !== this.picked_salesperson){
                    status = false;
                }
            }
            if (this.picked_sale_type === 'layaway') {
                if (!item.is_layaway && !item.was_layaway && !item.layaway_sales_amount) {
                    status = false;
                }
            } else if (this.picked_sale_type === 'regular') {
                if (item.is_layaway || item.was_layaway || item.layaway_sales_amount) {
                    status = false;
                }
            }
            return status;
        },

        set_dates: function(value){
            let start = null;
            let end = null;

            if(value == 'today'){
                start = moment().format("MM-DD-YYYY");
                end = moment().format("MM-DD-YYYY");
            }
            if(value == 'yesterday'){
                start = moment().add(-1, 'days').format("MM-DD-YYYY");
                end = moment().add(-1, 'days').format("MM-DD-YYYY");
            }
            if(value == 'this_week'){
                start = moment().startOf('week').format("MM-DD-YYYY");
                end = moment().endOf('week').format("MM-DD-YYYY");
            }
            if(value == 'last_week'){
                start = moment().subtract(1, 'weeks').startOf('week').format("MM-DD-YYYY");
                end = moment().subtract(1, 'weeks').endOf('week').format("MM-DD-YYYY");
            }
            if(value == 'this_month'){
                start = moment().startOf('month').format("MM-DD-YYYY");
                end = moment().endOf('month').format("MM-DD-YYYY");
            }
            if(value == 'last_month'){
                start = moment().subtract(1, 'months').startOf('month').format("MM-DD-YYYY");
                end = moment().subtract(1, 'months').endOf('month').format("MM-DD-YYYY");
            }

            if (start && end) {
                this.date_range = [start, end];
                this.picked_date_range = value;
                this.fetch_init();
            }
        },

        getPreviousDateRange() {
            if (!this.date_range || this.date_range.length !== 2) return null;
            const startDate = moment(this.date_range[0], "MM-DD-YYYY");
            const endDate = moment(this.date_range[1], "MM-DD-YYYY");
            const daysDiff = endDate.diff(startDate, 'days') + 1;
            const previousStart = startDate.clone().subtract(daysDiff, 'days');
            const previousEnd = endDate.clone().subtract(daysDiff, 'days');
            return [
                previousStart.format("MM-DD-YYYY"),
                previousEnd.format("MM-DD-YYYY")
            ];
        },

        getPreviousDateRangeText() {
            const previousRange = this.getPreviousDateRange();
            return previousRange ? previousRange.join(' to ') : '—';
        },

        fetchComparisonData() {
            return new Promise((resolve) => {
                const previousDateRange = this.getPreviousDateRange();
                if (!previousDateRange) {
                    this.previousPeriodData = [];
                    resolve();
                    return;
                }

                let that = this;
                let data = {
                    dates: previousDateRange,
                    location_id: this.picked_location_id,
                    picked_invoice_type: this.picked_invoice_type,
                    details_filter: this.details_filter,
                    sale_type: null,
                    salesperson: this.picked_salesperson,
                    username: this.packet.userDetails.username,
                    include_sales_count: true
                };

                let path = "/api/reports/reports_users_sale/";
                that.comparisonLoading = true;

                let successCallback = function(response) {
                    if (response && response.data && response.data.result) {
                        that.previousPeriodData = response.data.result;
                    } else {
                        that.previousPeriodData = [];
                    }
                    that.comparisonLoading = false;
                    resolve();
                }

                let errorCallback = function(error) {
                    console.error("Error fetching comparison data:", error);
                    that.previousPeriodData = [];
                    that.comparisonLoading = false;
                    resolve();
                }

                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            });
        },

        fetch_init() {
            if (!this.details_filter || this.details_filter === "null") {
                this.$bvToast.toast('Please select a report type.', {
                    title: 'Invalid Filter', variant: 'warning', solid: true
                });
                return;
            }
            if (!this.date_range || this.date_range.length !== 2){
                this.$bvToast.toast('Please select a date range.', {
                    title: 'Invalid Date Range', variant: 'warning', solid: true
                });
                return;
            }

            let dates = this.date_range;
            let location_id = this.picked_location_id;
            let salesperson = this.picked_salesperson;

            let that = this;
            let data = {
                dates: dates,
                location_id: location_id,
                picked_invoice_type: this.picked_invoice_type,
                details_filter: this.details_filter,
                sale_type: this.picked_sale_type,
                salesperson: salesperson,
                username: this.packet.userDetails.username,
                include_sales_count: true
            };

            let path = "/api/reports/reports_users_sale/";
            this.items_being_loaded = true;

            this.fetchComparisonData().then(() => {
                let successCallback = function(response) {
                    if (response.data.result){
                        that.items = response.data.result;
                        that.derive_sorted_columns();
                    } else {
                        that.$bvToast.toast(
                            response.data.error,
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    that.items_being_loaded = false;
                }

                let errorCallback = function(error) {
                    console.log(error);
                    that.$bvToast.toast(
                        "Error occurred. Please try again.",
                        {title: "Error", autoHideDelay: 1500, variant:"danger"}
                    );
                    that.items_being_loaded = false;
                }

                Requestor.postSyncP(path, data, this, successCallback, errorCallback);
            });
        },

        derive_sorted_columns: function(){
            if (this.items.length > 0){
                let dcs = Object.keys(this.items[0]);
                var columns = ["salesperson", "location_id", "total_sales_count", "total_without_tax", "total_refunded_without_tax", "total_sales_tax", "total_refunded_st"];

                if (dcs.includes('layaway_sales_amount')) {
                    columns.push('layaway_sales_amount', 'regular_sales_amount');
                }

                let total_sales_prefixes = dcs.filter(function(d){ return d.split("_")[0] == "total" && d.split("_")[2] == "sales"});
                columns = columns.concat(total_sales_prefixes);

                let total_fsg_sales =  dcs.filter(function(d){ return d.split(" ")[0] == "SALES"});
                columns = columns.concat(total_fsg_sales);

                let total_refunds_prefixes = dcs.filter(function(d){ return d.split("_")[0] == "total" && d.split("_")[2] == "refunds"});
                columns = columns.concat(total_refunds_prefixes);

                let total_fsg_refunds =  dcs.filter(function(d){ return d.split(" ")[0] == "REFUNDS"});
                columns = columns.concat(total_fsg_refunds);

                this.columns = columns;
            }
        },

        sortByColumn(col) {
            if (this.sortColumn === col) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortColumn = col;
                this.sortDirection = 'asc';
            }
        },

        isSortableColumn(col) {
            return !['invoice_id', 'lease_ids', 'lease_company'].includes(col);
        },

        get_csv_data() {
            return this.items
                .filter(item => this.allowed_row(item))
                .map(item => {
                const row = {};
                this.allowed_cols().forEach(col => {
                    const header = this.fixed_columns_dict[col] || col;
                    if (col === 'salesperson') {
                        row[header] = this.formatSalespersonName(item[col]);
                    } else if (col === 'location_id') {
                        row[header] = this.packet.locationsdict[item[col]] || item[col];
                    } else if (['invoice_id', 'lease_ids', 'lease_company'].includes(col)) {
                        row[header] = item[col] || '—';
                    } else if (col === 'total_sales_count') {
                        row[header] = this.formatSalesCount(item[col]);
                    } else if (col === 'is_layaway') {
                        row[header] = item[col] ? 'Layaway' : 'Regular';
                    } else {
                        const num = item[col] != null ? parseFloat(item[col]) : 0;
                        row[header] = num.toFixed(2);
                    }
                });
                return row;
                });
        },

        get_csv_filename() {
            let now = moment().format("YYYY-MM-DD_HHmmss");
            let name_prefix = "salespeople_summary";
            if (this.details_filter == 2) {
                name_prefix = "salespeople_detailed_refunds";
            } else if (this.details_filter == 3) {
                name_prefix = "salespeople_detailed_sales";
            } else if (this.details_filter == 4) {
                name_prefix = "salespeople_per_invoice_sales";
            }
            if (this.picked_sale_type === 'layaway') {
                name_prefix += '_layaway';
            } else if (this.picked_sale_type === 'regular') {
                name_prefix += '_regular';
            }
            return `${name_prefix.toUpperCase()}_${now}.csv`;
        },

        getTableTitle() {
            const titles = {
                1: 'Salespeople Summary',
                2: 'Salespeople Detailed Refunds',
                3: 'Salespeople Detailed Sales',
                4: 'Salespeople Per Invoice Sales'
            };
            let title = titles[this.details_filter] || 'Salespeople Reports';
            if (this.picked_sale_type === 'layaway') {
                title += ' - Layaway Sales';
            } else if (this.picked_sale_type === 'regular') {
                title += ' - Regular Sales';
            }
            return title;
        },

        formatCurrency(value) {
            const num = parseFloat(value || 0);
            return num.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },

        formatSalesCount(value) {
            const count = parseInt(value || 0);
            return count.toLocaleString('en-US');
        },

        formatSalespersonName(username) {
            if (!username) return '—';
            return username.split('.').map(part =>
                part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
            ).join(' ');
        },

        formatLocationUser(item) {
            const locationName = this.packet.locationsdict[item.location_id] || `Location ${item.location_id}`;
            const salespersonName = this.formatSalespersonName(item.salesperson);
            return `${locationName} | ${salespersonName}`;
        },

        getHeaderClass(col) {
            const numericCols = [
                'total_sales_count', 'total_without_tax', 'net_sales', 'total_refunded_without_tax',
                'total_sales_tax', 'total_refunded_st', 'total_cc_sales', 'total_finance_sales',
                'total_cash_sales', 'layaway_sales_amount', 'layaway_paid_so_far', 'regular_sales_amount'
            ];
            if (numericCols.includes(col)) return 'text-end';
            if (col === 'salesperson') return 'text-start';
            return 'text-center';
        },

        getHeaderStyle(col) {
            if (col === 'salesperson') return 'min-width: 180px;';
            if (col === 'locuser') return 'min-width: 250px;';
            if (['invoice_id', 'lease_ids', 'lease_company'].includes(col)) return 'min-width: 120px;';
            if (col === 'total_sales_count') return 'min-width: 100px;';
            if (col === 'is_layaway') return 'min-width: 90px;';
            if (['layaway_sales_amount', 'regular_sales_amount'].includes(col)) return 'min-width: 120px;';
            return 'min-width: 100px;';
        },

        getCellClass(col, value) {
            let classes = [];
            const numericCols = [
                'total_sales_count', 'total_without_tax', 'net_sales', 'total_refunded_without_tax',
                'total_sales_tax', 'total_refunded_st', 'total_cc_sales', 'total_finance_sales',
                'total_cash_sales', 'layaway_sales_amount', 'layaway_paid_so_far', 'regular_sales_amount','total_short_amount'
            ];
            if (numericCols.includes(col)) classes.push('text-end');
            if (col === 'salesperson') classes.push('text-start');
            if (col.includes('refund') && parseFloat(value) > 0) classes.push('text-danger');
            if (col === 'total_short_amount' && parseFloat(value) > 0) classes.push('text-danger');
            if (col.includes('sales') && parseFloat(value) > 0 && col !== 'total_sales_count') classes.push('text-success');
            if (col === 'total_sales_count' && parseInt(value) > 0) classes.push('text-info');
            if (col === 'layaway_sales_amount' && parseFloat(value) > 0) classes.push('text-warning');
            if (col === 'layaway_paid_so_far' && parseFloat(value) > 0) classes.push('text-success');
            if (col === 'regular_sales_amount' && parseFloat(value) > 0) classes.push('text-primary');
            if (col === 'net_sales') {
                const netVal = parseFloat(value);
                if (netVal > 0) classes.push('text-success');
                else if (netVal < 0) classes.push('text-danger');
            }
            return classes.join(' ');
        },

        getRowClass(item) {
            const netAmount = parseFloat(item.total_without_tax || 0) - parseFloat(item.total_refunded_without_tax || 0);
            if (netAmount < 0) return 'table-danger-row';
            if (netAmount > 10000) return 'table-success-row';
            if (item.is_layaway === true || item.was_layaway === true || (item.layaway_sales_amount && item.layaway_sales_amount > 0)) return 'table-warning-row';
            return '';
        },

        isNumericColumn(col) {
            const nonNumericCols = ['salesperson', 'locuser', 'location_id', 'invoice_id', 'lease_ids', 'lease_company', 'is_layaway'];
            return !nonNumericCols.includes(col);
        },

        columnTotal(col) {
            if (!this.isNumericColumn(col)) return 0;
            return this.filteredItems.reduce((sum, item) => sum + parseFloat(item[col] || 0), 0);
        },

        getDateRangeText() {
            if (!this.date_range || this.date_range.length !== 2) return '';
            return `${this.date_range[0]} to ${this.date_range[1]}`;
        }
    },
    created: function(){
        this.packet = JSON.parse(localStorage.packet);
        this.packet.userDetails = JSON.parse(localStorage.userDetails);
        const startOfMonth = moment().startOf('month').format("MM-DD-YYYY");
        const today = moment().format("MM-DD-YYYY");
        this.date_range = [startOfMonth, today];
    },
}
</script>

<style scoped>
/* =========================================================
   GLOBAL
   ========================================================= */
.dashboard-container {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    min-height: 100vh;
    padding: 24px;
}

.main-card {
    border: none;
    border-radius: 20px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
    overflow: hidden;
}

/* =========================================================
   HEADER
   ========================================================= */
.header-body {
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    color: white;
}

.header-icon-wrapper {
    width: 56px;
    height: 56px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.header-icon {
    font-size: 28px;
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.08); }
}

.header-title {
    font-size: 32px;
    font-weight: 700;
    letter-spacing: -0.5px;
}

.header-subtitle {
    font-size: 16px;
    opacity: 0.8;
}

.date-badge {
    background-color: rgba(255, 255, 255, 0.15);
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
}

/* =========================================================
   FILTERS
   ========================================================= */
.filters-card {
    border: none;
    border-top: 1px solid #e8edf3;
    background: white;
}

.filter-label {
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.5px;
}

.custom-datepicker {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 10px 16px;
    font-size: 18px;
    width: 100%;
}

.quick-date-btn {
    border-radius: 20px;
    font-size: 16px;
    padding: 10px 18px;
    font-weight: 500;
}

.filter-select {
    border-radius: 12px;
    background: #f8fafc;
    border-color: #e2e8f0;
    font-size: 18px;
    padding: 10px 16px;
}

.search-btn {
    border-radius: 12px;
    padding: 12px 36px;
    font-weight: 700;
    font-size: 18px;
    letter-spacing: 0.5px;
}

.advanced-toggle {
    font-size: 16px;
    font-weight: 500;
}

/* =========================================================
   COMPARISON CARDS - REFERENCE STYLE (OPTION A)
   ========================================================= */
.comparison-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    padding: 24px;
}

.comparison-card {
    background: #fff;
    border-radius: 20px;
    padding: 32px 26px 24px;
    border: 1px solid #e8edf3;
    display: flex;
    flex-direction: column;
    transition: all 0.2s ease;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
    position: relative;
    overflow: hidden;
}

.comparison-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #6366f1, #8b5cf6);
}

.comparison-card:hover {
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.12);
    transform: translateY(-2px);
}

.comparison-card.dark {
    background: #0b1220;
    border-color: #0b1220;
    box-shadow: 0 8px 26px rgba(11, 18, 32, 0.35);
}

.comparison-card.dark .comparison-title { color: #fff; }
.comparison-card.dark .period-label { color: rgba(255, 255, 255, 0.6); }
.comparison-card.dark .period-box { background: rgba(255, 255, 255, 0.05); border-color: rgba(255, 255, 255, 0.1); }
.comparison-card.dark .period-value.secondary-value { color: rgba(255, 255, 255, 0.7); }
.comparison-card.dark .metric-box { background: rgba(255, 255, 255, 0.05); border-color: rgba(255, 255, 255, 0.1); }
.comparison-card.dark .metric-label { color: rgba(255, 255, 255, 0.6); }
.comparison-card.dark .date-info { color: rgba(255, 255, 255, 0.5); border-top-color: rgba(255, 255, 255, 0.1); }
.comparison-card.dark .date-info strong { color: rgba(255, 255, 255, 0.7); }

/* Accent stripe colors */
.accent-green::before   { background: linear-gradient(90deg, #10b981, #34d399); }
.accent-red::before     { background: linear-gradient(90deg, #ef4444, #f87171); }
.accent-amber::before   { background: linear-gradient(90deg, #f59e0b, #fbbf24); }

/* Card header */
.comparison-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 24px;
}

.comparison-icon-wrap {
    width: 76px;
    height: 76px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 34px;
    margin-bottom: 16px;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.icon-green   { background: linear-gradient(135deg, #10b981, #34d399); }
.icon-red     { background: linear-gradient(135deg, #ef4444, #f87171); }
.icon-amber   { background: linear-gradient(135deg, #f59e0b, #fbbf24); }

.comparison-title {
    font-size: 22px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    text-align: center;
    margin: 0;
    color: #0b1220;
}

/* Periods row */
.periods-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 20px;
}

.period-box {
    text-align: center;
    padding: 18px 12px;
    background: #f8fafc;
    border-radius: 12px;
    border: 1px solid #e9ecef;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.period-label {
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 10px;
}

.period-value {
    font-size: 22px;
    font-weight: 800;
    line-height: 1.15;
    letter-spacing: -0.5px;
    word-break: break-word;
}

.period-value.primary-value { color: #0b1220; }
.period-value.secondary-value { color: #64748b; font-weight: 700; }
.period-value.refund-value { color: #ef4444; }
.period-value.layaway-value { color: #f59e0b; }

.comparison-card.dark .period-value.primary-value { color: #ffffff; }

/* Metrics row */
.metrics-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 20px;
}

.metric-box {
    text-align: center;
    padding: 14px 10px;
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e9ecef;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.metric-label {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
}

.metric-value {
    font-size: 18px;
    font-weight: 800;
    line-height: 1.2;
    word-break: break-word;
}

/* Date info */
.date-info {
    font-size: 12px;
    text-align: center;
    margin-top: auto;
    padding-top: 14px;
    border-top: 1px solid #e9ecef;
    color: #94a3b8;
    line-height: 1.5;
}

.date-info strong { color: #475569; }

/* =========================================================
   DATA TABLE
   ========================================================= */
.data-table-card {
    border: none;
    border-top: 1px solid #e8edf3;
    background: #fff;
    box-shadow: none;
    border-radius: 0;
}

.table-title {
    font-size: 20px;
    font-weight: 600;
}

.table-badge {
    font-size: 14px;
    padding: 6px 12px;
}

.table-wrapper-sm {
    overflow-x: auto;
}

.compact-table {
    width: 100%;
    font-size: 16px;
    border-collapse: collapse;
}

.compact-table th,
.compact-table td {
    padding: 14px 16px;
    border: 1px solid #dee2e6;
    font-size: 16px;
}

.compact-table th {
    background: #c3cdd3;
    font-weight: 700;
    position: sticky;
    top: 0;
    white-space: nowrap;
    font-size: 16px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.compact-table td {
    white-space: nowrap;
}

.compact-table tbody tr:hover {
    background: #e0e7ff !important;
}

.table-success-row {
    background: linear-gradient(90deg, rgba(16, 185, 129, 0.05), transparent);
}

.table-danger-row {
    background: linear-gradient(90deg, rgba(239, 68, 68, 0.05), transparent);
}

.table-warning-row {
    background: linear-gradient(90deg, rgba(245, 158, 11, 0.05), transparent);
}

.totals-row {
    background: #fef3c7;
    font-weight: 600;
}

.totals-row td { background: #fef3c7; }

.text-end { text-align: right !important; }
.text-start { text-align: left !important; }
.text-center { text-align: center !important; }
.text-danger { color: #dc3545 !important; }
.text-success { color: #28a745 !important; }
.text-primary { color: #6366f1 !important; }
.text-warning { color: #f59e0b !important; }
.text-info { color: #06b6d4 !important; }
.text-muted { color: #6c757d !important; }

.delivery-badge {
    font-size: 14px;
    padding: 4px 12px;
}

.sort-btn {
    text-decoration: none;
    color: #64748b;
}

.sort-btn:hover { text-decoration: none; }

.pagination-wrapper {
    border-top: 1px solid #e2e8f0;
}

/* =========================================================
   ANIMATIONS
   ========================================================= */
@keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}

.comparison-card, .data-table-card, .filters-card {
    animation: fadeIn 0.5s ease-out;
}

.border-top { border-top: 1px solid #e2e8f0; }

/* =========================================================
   RESPONSIVE
   ========================================================= */
@media (max-width: 1200px) {
    .comparison-grid { grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .period-value { font-size: 18px; }
    .metric-value { font-size: 15px; }
}

@media (max-width: 992px) {
    .dashboard-container { padding: 16px; }
    .header-title { font-size: 24px; }
    .comparison-grid { grid-template-columns: 1fr; gap: 16px; padding: 16px; }
    .comparison-icon-wrap { width: 64px; height: 64px; font-size: 28px; margin-bottom: 12px; }
    .comparison-title { font-size: 18px; }
    .period-value { font-size: 20px; }
    .compact-table th, .compact-table td { padding: 10px 12px; font-size: 14px; }
    .table-title { font-size: 16px; }
}

@media (max-width: 576px) {
    .dashboard-container { padding: 10px; }
    .comparison-grid { padding: 12px; gap: 12px; }
    .comparison-card { padding: 24px 18px 20px; }
    .periods-row, .metrics-row { gap: 8px; }
    .period-value { font-size: 16px; }
    .metric-value { font-size: 13px; }
    .compact-table th, .compact-table td { padding: 8px 10px; font-size: 12px; }
    .header-title { font-size: 18px; }
    .header-subtitle { font-size: 12px; }
    .comparison-title { font-size: 16px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Reports/ReportsUsersSale.vue